import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  IListAssignee,
  IListCriteriaGroup,
  IListField,
  IListFieldCatalogue,
  IListOrder,
  ListCriteriaNode,
  ListFieldSource
} from '../../../api/lists-contracts';
import {
  emptyRoot,
  readCriteria,
  writeCriteria,
  type ICriteriaGroup,
  type ICriteriaWords,
  type KindOf
} from '../../../list-criteria';
import type { IFunnelOpen } from '../grid';
import { useCatalogueLookups, useCriteriaWords } from '../use-catalogue';
import { dropPaging } from './pager';

/**
 * A list-backed work page's Lists controls (SR-456) — Reinstatement, DD calls, Metrics updates: the
 * filter tree and a one-column sort, in the URL beside the page's own controls, which stay; the
 * funnel open and *Advanced…*, the screen's. The columns are the viewer's (`columns.tsx`).
 *
 * - `f` — the filter tree as survey-api reads it (`writeCriteria`), ANDed onto the page's own
 *   criteria (`filter`).
 * - `sort` — `code` or `code:desc`, one column at a time as in Lists; none keeps the page's order.
 */
export interface IWorkPage {
  survey: string;
  catalogue: IListFieldCatalogue;
  fields: ReadonlyMap<string, IListField>;
  fieldOf: (code: string) => IListField | undefined;
  sourceOf: (code: string) => ListFieldSource | undefined;
  kindOf: KindOf;
  words: ICriteriaWords;
  /** The assignable accounts, for *Advanced…*'s person values — undefined until read. */
  accounts: readonly IListAssignee[] | undefined;
  root: ICriteriaGroup;
  setRoot: (root: ICriteriaGroup) => void;
  /** The tree as survey-api reads it — null while it holds nothing. */
  criteria: Nullable<IListCriteriaGroup>;
  /** The tree as one node to AND into the page's criteria — null while it holds nothing. */
  filter: Nullable<ListCriteriaNode>;
  sorts: IListOrder[];
  onSort: (sorts: IListOrder[]) => void;
  funnel: Nullable<IFunnelOpen>;
  setFunnel: (open: Nullable<IFunnelOpen>) => void;
  advanced: { open: boolean; focusId?: string };
  setAdvanced: (advanced: { open: boolean; focusId?: string }) => void;
}

export interface IWorkPageOptions {
  /** The URL's keys a change drops — the page's paging, so a new filter starts at the top. */
  drop?: readonly string[];
  /** Replace the history entry rather than push one — the page's own controls' way. */
  replace?: boolean;
}

/** The tree as the URL's `f` spells it — null while it holds nothing. */
export function filterParam(root: ICriteriaGroup, kindOf: KindOf): Nullable<string> {
  return root.children.length > 0 ? JSON.stringify(writeCriteria(root, kindOf)) : null;
}

/** The tree the URL's `f` holds; one that does not read is no filter. */
function treeOf(written: Nullable<string>): ICriteriaGroup {
  if (!written) return emptyRoot();
  try {
    return readCriteria(JSON.parse(written));
  } catch {
    return emptyRoot();
  }
}

export function useWorkPage(
  survey: string,
  catalogue: IListFieldCatalogue,
  options: IWorkPageOptions = {}
): IWorkPage {
  const { fields, fieldOf, sourceOf, kindOf, assignees, words: lookupWords } = useCatalogueLookups(catalogue);
  const accounts = assignees.data ?? undefined;
  const [params, setParams] = useSearchParams();
  const held = React.useRef(options);
  held.current = options;

  const written = params.get('f');
  const sortWritten = params.get('sort');
  const root = React.useMemo(() => treeOf(written), [written]);
  const words = useCriteriaWords(survey, root, lookupWords);
  const criteria = React.useMemo(() => (root.children.length > 0 ? writeCriteria(root, kindOf) : null), [root, kindOf]);
  const filter = React.useMemo<Nullable<ListCriteriaNode>>(
    () => (criteria ? { op: criteria.op, children: criteria.children } : null),
    [criteria]
  );
  const sorts = React.useMemo<IListOrder[]>(() => {
    if (!sortWritten) return [];
    const [field, dir] = sortWritten.split(':');
    return [{ field, dir: dir === 'desc' ? 'desc' : 'asc' }];
  }, [sortWritten]);

  const write = React.useCallback(
    (changes: Record<string, Nullable<string>>) => {
      const { drop = [], replace = false } = held.current;
      setParams(
        (now) => {
          const next = new URLSearchParams(now);
          for (const [key, value] of Object.entries(changes)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          for (const key of drop) next.delete(key);
          return next;
        },
        { replace }
      );
    },
    [setParams]
  );
  const setRoot = React.useCallback(
    (next: ICriteriaGroup) => write({ f: filterParam(next, kindOf) }),
    [write, kindOf]
  );
  const onSort = React.useCallback(
    ([first]: IListOrder[]) =>
      write({ sort: first ? (first.dir === 'desc' ? `${first.field}:desc` : first.field) : null }),
    [write]
  );

  const [funnel, setFunnel] = React.useState<Nullable<IFunnelOpen>>(null);
  const [advanced, setAdvanced] = React.useState<{ open: boolean; focusId?: string }>({ open: false });

  return React.useMemo(
    () => ({
      survey,
      catalogue,
      fields,
      fieldOf,
      sourceOf,
      kindOf,
      words,
      accounts,
      root,
      setRoot,
      criteria,
      filter,
      sorts,
      onSort,
      funnel,
      setFunnel,
      advanced,
      setAdvanced
    }),
    [
      survey,
      catalogue,
      fields,
      fieldOf,
      sourceOf,
      kindOf,
      words,
      accounts,
      root,
      setRoot,
      criteria,
      filter,
      sorts,
      onSort,
      funnel,
      advanced
    ]
  );
}

/** What `useWorkSearch` answers: the URL's writer and the search box's draft. */
export interface IWorkSearch {
  /** The search the URL holds (`q`). */
  q: string;
  /** Writes `changes` — a list one key per value, `null` or `''` dropping the key — replacing the
   *  history entry, and drops the window: a new filter starts at the top. */
  set: (changes: Record<string, Nullable<string> | readonly string[]>) => void;
  /** The search box's draft, which follows the URL's `q`, and its submit, which writes it. */
  search: { draft: string; onDraft: (draft: string) => void; onSubmit: () => void };
}

/** A list-backed work page's own controls in the URL (SR-485) — DD calls, Metrics updates. */
export function useWorkSearch(): IWorkSearch {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [draft, setDraft] = React.useState(q);
  React.useEffect(() => setDraft(q), [q]);
  const set = React.useCallback(
    (changes: Record<string, Nullable<string> | readonly string[]>) =>
      setParams(
        (now) => {
          const next = dropPaging(new URLSearchParams(now));
          for (const [key, value] of Object.entries(changes)) {
            next.delete(key);
            for (const one of value === null ? [] : typeof value === 'string' ? [value] : value) {
              if (one !== '') next.append(key, one);
            }
          }
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  return { q, set, search: { draft, onDraft: setDraft, onSubmit: () => set({ q: draft.trim() || null }) } };
}
